export const PLAYER_VARIANT_STORAGE_KEY = 'playerVariant';
export const PROFILE_REFRESH_TIMEOUT_MS = 2500;

const SUPPORTED_VARIANTS = new Set(['A', 'B', 'C', 'D']);

/**
 * Normalize the backend playback assignment while remaining compatible with
 * older `/me` responses that did not expose a variant yet.
 */
export function normalizePlayerVariant(value) {
  const normalized = String(value || 'A')
    .trim()
    .toUpperCase();
  return SUPPORTED_VARIANTS.has(normalized) ? normalized : 'A';
}

export function isUltraPlayerVariant(value) {
  return normalizePlayerVariant(value) === 'C';
}

export function isDPlayerVariant(value) {
  return normalizePlayerVariant(value) === 'D';
}

/**
 * Settle the server-selected transport before mounting a player, without ever
 * making the public playback path depend indefinitely on the authenticated
 * profile API. The refresh receives an AbortSignal and the independent race
 * also protects callers whose refresh implementation ignores aborts.
 *
 * A timeout or profile error falls back to the caller-provided cached
 * assignment (both HLS profiles can consume each other's relay sessions),
 * defaulting to C: Ultra can negotiate a successful legacy MPEG-TS response,
 * while mpegts.js cannot consume HLS.
 */
export async function resolvePlayerVariantBeforePlayback(
  refreshProfile,
  timeoutMs = PROFILE_REFRESH_TIMEOUT_MS,
  fallbackVariant = 'C'
) {
  const fallback = SUPPORTED_VARIANTS.has(fallbackVariant) ? fallbackVariant : 'C';
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId = null;

  const refreshResult = Promise.resolve()
    .then(() => refreshProfile(controller ? {signal: controller.signal} : {}))
    .then(
      (profile) => normalizePlayerVariant(profile?.playerVariant || fallback),
      () => fallback
    );
  const timeoutResult = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      if (controller) controller.abort();
      resolve(fallback);
    }, Math.max(0, timeoutMs));
  });

  try {
    return await Promise.race([refreshResult, timeoutResult]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }
}
